/* ------------------------------------------------------------------
   ApiKeysPage.js  (v2.0 – modernized UI, matches dashboard theme)
------------------------------------------------------------------ */
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Trash, Clipboard } from 'react-bootstrap-icons'

const sectionCard = {
  background  : '#ffffff',
  border      : '1px solid #e5e7eb',
  borderRadius: 12,
  padding     : '22px 24px',
  marginBottom: 20,
  boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
}

export default function ApiKeysPage () {
  const [apiKeys,           setApiKeys]           = useState([])
  const [isLoading,         setIsLoading]         = useState(true)
  const [error,             setError]             = useState('')
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null)
  const [copied,            setCopied]            = useState(false)

  const fetchApiKeys = async () => {
    setIsLoading(true); setError('')
    try {
      const response = await axios.get('/api/api-keys', { withCredentials: true })
      setApiKeys(response.data.api_keys || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch API keys. This feature may require a Premium subscription.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { fetchApiKeys() }, [])

  const handleGenerateKey = async () => {
    const keyName = prompt('Enter a name for your new API key (e.g., "Zapier Integration"):', 'Zapier Key')
    if (!keyName?.trim()) return
    setNewlyGeneratedKey(null); setError('')
    try {
      const response = await axios.post('/api/api-keys', { name: keyName }, { withCredentials: true })
      setNewlyGeneratedKey(response.data.api_key)
      fetchApiKeys()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create API key.')
    }
  }

  const handleDeleteKey = async keyId => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) return
    try {
      await axios.delete(`/api/api-keys/${keyId}`, { withCredentials: true })
      fetchApiKeys()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete API key.')
    }
  }

  const handleCopy = () => {
    if (!newlyGeneratedKey) return
    navigator.clipboard.writeText(newlyGeneratedKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 4 }}>API Keys & Integrations</h2>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>Connect Greatly Social to external services like Zapier.</p>
        </div>
        <button
          onClick={handleGenerateKey}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '10px 18px', borderRadius: 9, border: 'none',
            background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
          onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
        >
          <i className='fas fa-plus' style={{ fontSize: 11 }} />
          Generate New Key
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#dc2626', fontWeight:500 }}>
          <i className='fas fa-exclamation-circle me-2' />{error}
        </div>
      )}

      {/* New key reveal */}
      {newlyGeneratedKey && (
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <i className='fas fa-check-circle' style={{ color:'#16a34a', fontSize:16 }} />
            <span style={{ fontSize:14, fontWeight:700, color:'#15803d' }}>New API Key Generated!</span>
          </div>
          <p style={{ fontSize:13, color:'#166534', marginBottom:14, lineHeight:1.5 }}>
            Copy this key now — you won't be able to see it again.
          </p>
          <div style={{ display:'flex', gap:8 }}>
            <input
              type='text'
              readOnly
              value={newlyGeneratedKey}
              style={{
                flex:1, padding:'9px 12px', borderRadius:8,
                border:'1.5px solid #bbf7d0', background:'#ffffff',
                fontFamily:'monospace', fontSize:13, color:'#111827', outline:'none',
              }}
            />
            <button
              onClick={handleCopy}
              style={{
                display:'flex', alignItems:'center', gap:6,
                padding:'9px 14px', borderRadius:8, border:'none',
                background: copied ? '#16a34a' : '#2563eb',
                color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
                transition:'background 0.15s',
              }}
            >
              {copied ? <><i className='fas fa-check' style={{ fontSize:11 }} /> Copied!</> : <><Clipboard size={13} /> Copy Key</>}
            </button>
          </div>
        </div>
      )}

      {/* Keys list */}
      <div style={sectionCard}>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Your API Keys</h3>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
            Keys are masked for security — only the last few characters are shown.
          </p>
        </div>

        {isLoading ? (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'20px 0', color:'#9ca3af', fontSize:13 }}>
            <div className='spinner-border spinner-border-sm' style={{ color:'#d1d5db' }} />
            Loading keys…
          </div>
        ) : apiKeys.length > 0 ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {apiKeys.map(key => (
              <div
                key={key.id}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'14px 16px', borderRadius:10,
                  border:'1.5px solid #e5e7eb', background:'#fafafa',
                  gap:12, flexWrap:'wrap',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{
                    width:36, height:36, borderRadius:8, background:'#eff6ff',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                  }}>
                    <i className='fas fa-key' style={{ fontSize:14, color:'#2563eb' }} />
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>{key.name}</div>
                    <div style={{ fontSize:12, fontFamily:'monospace', color:'#9ca3af', marginTop:2 }}>
                      {key.api_key}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteKey(key.id)}
                  style={{
                    display:'flex', alignItems:'center', gap:6,
                    padding:'7px 12px', borderRadius:7,
                    border:'1.5px solid #fecaca', background:'#fef2f2',
                    color:'#dc2626', fontSize:12, fontWeight:600, cursor:'pointer',
                    transition:'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background='#ef4444'; e.currentTarget.style.color='#fff'; e.currentTarget.style.borderColor='#ef4444' }}
                  onMouseLeave={e => { e.currentTarget.style.background='#fef2f2'; e.currentTarget.style.color='#dc2626'; e.currentTarget.style.borderColor='#fecaca' }}
                >
                  <Trash size={12} />
                  Delete
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'36px 24px', color:'#9ca3af' }}>
            <i className='fas fa-key' style={{ fontSize:28, marginBottom:10, display:'block', opacity:0.4 }} />
            <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>No API keys yet</div>
            <div style={{ fontSize:13 }}>Generate your first key to connect external services.</div>
          </div>
        )}
      </div>

      {/* Info card */}
      <div style={{ ...sectionCard, background:'#f8fafc' }}>
        <div style={{ display:'flex', gap:12 }}>
          <i className='fas fa-info-circle' style={{ color:'#2563eb', fontSize:16, marginTop:2, flexShrink:0 }} />
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:4 }}>Using your API key</div>
            <p style={{ fontSize:13, color:'#6b7280', margin:0, lineHeight:1.6 }}>
              Include your API key in the <code style={{ background:'#e5e7eb', padding:'1px 5px', borderRadius:4, fontSize:12 }}>x-api-key</code> header
              when making requests to the Greatly Social API. Keep your keys secret — treat them like passwords.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}